import TreeView16 from '@carbon/icons-react/es/tree-view/16';
import { Card as AntCard, Spin, Tooltip } from 'antd';
import dropRight from 'lodash/dropRight';
import React, { FC, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import { CompanyId, TaskId } from 'api/models';
import { Task } from 'api/tasks';
import { ReactComponent as AtRisk16 } from 'assets/svgs/at-risk.svg';
import { ReactComponent as FormTemplateIconSvg } from 'assets/svgs/form-instance-icon.svg';
import { Avatar, AvatarGroup } from 'components/atoms/avatar';
import { IconShell } from 'components/atoms/icon-shell';
import { withPrivacy } from 'components/atoms/private';
import { StyledCSSSpinner } from 'components/organisms/resource-management/scheduler-config/columns/cells/component';
import {
  BodyShort01,
  Caption01,
  HelperText01,
  MultiLineEllipsis,
  SingleLineEllipsis,
  Text01,
  Text02,
} from 'components/styles';
import { RootState } from 'data';
import { useStatusField } from 'data/fields/hooks/account-fields';
import { useNavigateToTask } from 'hooks/use-navigate-to-task';
import { useProjectsFromInitData } from 'queries/account';
import { CompaniesStore } from 'stores/companies';
import { TaskStatusPicker } from '../../atoms/tasks';
import { CardDimension } from '../../organisms/kanban-board/common';
import { FooterAssigneeWithDate } from '../../organisms/kanban-board/footer-assignee-with-date';

const Card = withPrivacy(
  styled(AntCard)<{ pointer: boolean }>`
    width: ${CardDimension.widthPx};
    height: ${CardDimension.heightPx};
    .ant-card-body {
      display: flex;
      height: 100%;
      padding: var(--spacing-04) var(--spacing-05);
      position: relative;
    }
    cursor: ${({ pointer }) => (pointer ? 'pointer' : 'default')};
  `,
  css`
    background: var(--private-01);
  `,
);

const StatusCellWrapper = styled.div`
  display: flex;
`;

const Title = styled.div`
  ${BodyShort01};
  ${Text01};
  ${MultiLineEllipsis};
  gap: var(--spacing-02);
`;

const SubTitle = styled.div`
  ${Caption01};
  ${Text02};
  ${SingleLineEllipsis};
  flex: 1;
`;

const ProjectName = ({ projectId }) => {
  const { data: projectsData } = useProjectsFromInitData();
  const project = projectsData?.byId[projectId];

  return project ? <SubTitle>{project.projectName}</SubTitle> : null;
};

const AtRiskIcon = styled(AtRisk16)`
  position: absolute;
  left: 0;
  top: 10px;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: var(--spacing-04);
  width: calc(100% - 32px);
`;

const AlignFooterElements = css`
  align-items: center;
  display: flex;
`;

const TaskCardFooterWrapper = styled.div`
  ${HelperText01};
  ${Text02};
  ${AlignFooterElements};
  min-height: 24px;
`;

const TaskCardFooterLeft = styled.div`
  ${AlignFooterElements};
  flex: 1;
  padding-right: var(--spacing-03);
  position: relative;
`;

const TaskCardFooterRight = styled.div`
  ${AlignFooterElements};

  span {
    ${AlignFooterElements};
  }
`;

const StatusWrapper = styled.div`
  margin-top: -6px;
  margin-left: -6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
`;

const SpinnerWrapper = styled(Spin)`
  padding: var(--spacing-03) 0;
`;

type TaskCardProps = Record<string, any>;

export const TaskCard: FC<TaskCardProps> = ({
  taskName,
  progress,
  taskId,
  project,
  assignee,
  dueDate,
  startDate,
  atRisk,
  private: isPrivate,
  completedAt,
  type,
  status,
  responsible,
  taskTree,
  isLoading = false,
  formInstanceId,
  isUpdating = false,
}) => {
  const navigateToTask = useNavigateToTask();
  const openTask = () => navigateToTask(TaskId(project, taskId));
  const statusField = useStatusField();
  const companiesStore = useSelector(({ companies }: RootState) => companies);
  const fields = useMemo(
    () => [{ fieldId: statusField.fieldId, fieldValue: status }],
    [statusField, status],
  );
  const treeTile = dropRight(taskTree as Task[], 1)
    .map((x) => x.taskName)
    .join(' / ');

  return (
    <Card
      loading={isLoading}
      bordered={false}
      hoverable={!isUpdating}
      private={isPrivate}
      onClick={openTask}
      pointer={!isUpdating}
    >
      <StatusWrapper>
        <StatusCellWrapper>
          {atRisk && <AtRiskIcon />}
          {isUpdating ? (
            <SpinnerWrapper indicator={<StyledCSSSpinner />} />
          ) : (
            <TaskStatusPicker
              taskId={taskId}
              projectId={project}
              type={type}
              progress={progress}
              fields={fields}
            />
          )}
        </StatusCellWrapper>
        {taskTree.length > 1 ? (
          <IconShell
            size="small"
            icon={
              <Tooltip
                title={
                  <FormattedMessage
                    id="task_card.subtask.tooltip"
                    values={{ taskName: treeTile }}
                  />
                }
              >
                <TreeView16 />
              </Tooltip>
            }
          />
        ) : null}
      </StatusWrapper>
      <ContentWrapper>
        <Title title={taskName} line={2}>
          {formInstanceId ? (
            <span>
              <FormTemplateIconSvg />
            </span>
          ) : null}
          {taskName}
        </Title>
        <ProjectName projectId={project} />
        <TaskCardFooterWrapper>
          <TaskCardFooterLeft>
            <FooterAssigneeWithDate
              assignee={assignee}
              startDate={startDate}
              dueDate={dueDate}
              completedAt={completedAt}
            />
          </TaskCardFooterLeft>
          <TaskCardFooterRight>
            <AvatarGroup maxCount={2}>
              {(responsible ?? []).map((companyId) => {
                const company = CompaniesStore.ofId(CompanyId(companyId))(
                  companiesStore,
                );

                return company ? (
                  <Avatar
                    size="small"
                    key={company.companyId}
                    name={company.companyName}
                    src={company.companyLogoUrl}
                  />
                ) : null;
              })}
            </AvatarGroup>
          </TaskCardFooterRight>
        </TaskCardFooterWrapper>
      </ContentWrapper>
    </Card>
  );
};
